/** "Dive (optional)" on the post form: a dive from the diver's log file, or
 * its details typed in, shown as it will appear before posting.
 *
 * From a log file, the dives are listed on the diver's computer
 * (js/diveLog.js) and only the chosen one is sent: the rest of the file never
 * leaves it. A post about a dive event starts on the dive from the event's
 * day, otherwise on the newest.
 *
 * The details are typed in the diver's units, with a m / ft switch beside
 * them, and sent metric; the API's message about a detail shows beside it.
 */
import { api, ApiError } from "./api.js";
import { esc } from "./utils.js";
import { readLog, cutDive, preselectedDive, readFileText, DiveLogError } from "./diveLog.js";
import { diveProfileHtml, attachDiveProfiles, formatDiveStart, formatDiveDuration } from "./diveProfileChart.js";
import {
  IMPERIAL, METRIC, unitLabel, formatDepth, depthIn, depthToMetres, temperatureIn, temperatureToCelsius,
  pressureIn, pressureToBar,
} from "./units.js";

// The details' inputs: the API's field, the input, and how it converts.
const DETAILS = [
  { field: "started_at", id: "dive-when" },
  { field: "duration_s", id: "dive-duration" },
  { field: "max_depth_m", id: "dive-max-depth", quantity: "depth" },
  { field: "avg_depth_m", id: "dive-avg-depth", quantity: "depth" },
  { field: "water_temp_c", id: "dive-water-temp", quantity: "temperature" },
  { field: "o2", id: "dive-o2" },
  { field: "tank_start_bar", id: "dive-tank-start", quantity: "pressure" },
  { field: "tank_end_bar", id: "dive-tank-end", quantity: "pressure" },
];

const TO_DISPLAY = { depth: depthIn, temperature: temperatureIn, pressure: pressureIn };
const TO_METRIC = { depth: depthToMetres, temperature: temperatureToCelsius, pressure: pressureToBar };

function round(value, places) {
  const scale = 10 ** places;
  return Math.round(value * scale) / scale;
}

function input(id, label, { type = "number", step = "any", unit = "", hint = "" } = {}) {
  return `<div class="col-6 col-md-4">
    <label class="form-label small mb-1" for="${id}">${esc(label)}${unit ? ` <span class="dive-unit" data-unit-of="${id}">(${esc(unit)})</span>` : ""}</label>
    <input class="form-control form-control-sm" type="${type}" id="${id}"${type === "number" ? ` step="${step}" min="0"` : ""} />
    ${hint ? `<div class="form-text">${esc(hint)}</div>` : ""}
    <div class="invalid-feedback" data-error-for="${id}"></div>
  </div>`;
}

export function diveFieldHtml() {
  return `
    <div class="mb-3" id="dive-field">
      <div class="form-label">Dive (optional)</div>
      <div id="dive-choose" class="d-flex flex-wrap gap-2 align-items-center">
        <label class="btn btn-outline-secondary btn-sm mb-0" for="dive-file">
          <i class="bi bi-file-earmark-arrow-up me-1"></i>From a dive log
        </label>
        <input type="file" id="dive-file" class="d-none" accept=".uddf,.ssrf,.xml" />
        <button type="button" class="btn btn-outline-secondary btn-sm" id="dive-enter">
          <i class="bi bi-pencil me-1"></i>Enter details
        </button>
      </div>
      <div class="form-text" id="dive-hint">A UDDF file from Shearwater Cloud or Subsurface, or Subsurface's own
        dive log. Only the dive you choose is sent: the rest of the file stays on your computer.</div>
      <div id="dive-list" class="mt-2" hidden>
        <label class="form-label small mb-1" for="dive-choice">Choose the dive</label>
        <select class="form-select form-select-sm" id="dive-choice" size="6"></select>
        <div class="d-flex gap-2 mt-2">
          <button type="button" class="btn btn-primary btn-sm" id="dive-use">Use this dive</button>
          <button type="button" class="btn btn-outline-secondary btn-sm" id="dive-list-cancel">Cancel</button>
        </div>
      </div>
      <div id="dive-details" class="border rounded p-2 mt-2" hidden>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <div class="small text-slate">Duration and maximum depth are required.</div>
          <div class="btn-group btn-group-sm dive-units-switch" role="group" aria-label="Units to enter the dive in">
            <button type="button" class="btn btn-outline-secondary" data-entry-units="${METRIC}">m</button>
            <button type="button" class="btn btn-outline-secondary" data-entry-units="${IMPERIAL}">ft</button>
          </div>
        </div>
        <div class="row g-2">
          ${input("dive-when", "Date and time", { type: "datetime-local" })}
          ${input("dive-duration", "Duration", { unit: "min", step: "1" })}
          ${input("dive-max-depth", "Maximum depth", { unit: "m" })}
          ${input("dive-avg-depth", "Average depth", { unit: "m" })}
          ${input("dive-water-temp", "Lowest water temperature", { unit: "°C" })}
          <div class="col-6 col-md-4">
            <label class="form-label small mb-1" for="dive-gas">Gas</label>
            <select class="form-select form-select-sm" id="dive-gas">
              <option value="">Not set</option><option value="air">Air</option><option value="nitrox">Nitrox</option>
            </select>
          </div>
          <div class="col-6 col-md-4" id="dive-o2-wrap" hidden>
            <label class="form-label small mb-1" for="dive-o2">Oxygen (%)</label>
            <input class="form-control form-control-sm" type="number" id="dive-o2" min="21" max="100" step="1" />
            <div class="invalid-feedback" data-error-for="dive-o2"></div>
          </div>
          ${input("dive-tank-start", "Tank start pressure", { unit: "bar" })}
          ${input("dive-tank-end", "Tank end pressure", { unit: "bar" })}
        </div>
        <div class="d-flex gap-2 mt-2">
          <button type="button" class="btn btn-primary btn-sm" id="dive-details-preview">Preview dive</button>
          <button type="button" class="btn btn-outline-secondary btn-sm" id="dive-details-cancel">Cancel</button>
        </div>
      </div>
      <div id="dive-error" class="text-danger small mt-2" hidden></div>
      <div id="dive-preview" class="mt-2" hidden>
        <div id="dive-preview-chart"></div>
        <button type="button" class="btn btn-sm btn-outline-danger" id="dive-remove">
          <i class="bi bi-x-lg me-1"></i>Remove dive
        </button>
      </div>
    </div>`;
}

/** Wires the field. existing: the post's dive when editing. units: the
 * diver's. onLogDive(dive): called with the dive chosen from a log, as
 * readLog lists it (with its site's position), once its preview succeeds.
 * Returns {setEventDay(day), value()}: value() resolves to undefined
 * (leave the post's dive alone), null (remove it) or the dive to send. */
export function attachDiveField(container, { existing = null, units = METRIC, onLogDive = null } = {}) {
  const $ = (sel) => container.querySelector(sel);
  const choose = $("#dive-choose");
  const hint = $("#dive-hint");
  const list = $("#dive-list");
  const choice = $("#dive-choice");
  const details = $("#dive-details");
  const errorEl = $("#dive-error");
  const preview = $("#dive-preview");
  const fileInput = $("#dive-file");

  let log = null;
  let eventDay = null;
  let sending; // the dive to send, once previewed
  let removed = false; // the post's own dive taken off
  let pending = null; // a preview on its way
  let entryUnits = units;

  function show(state) {
    choose.hidden = hint.hidden = state !== "choose";
    choose.classList.toggle("d-flex", state === "choose");
    list.hidden = state !== "list";
    details.hidden = state !== "details";
    preview.hidden = state !== "preview";
  }

  function showError(message) {
    errorEl.textContent = message;
    errorEl.hidden = !message;
  }

  function clearFieldErrors() {
    container.querySelectorAll("#dive-details .is-invalid").forEach((el) => el.classList.remove("is-invalid"));
    container.querySelectorAll("#dive-details [data-error-for]").forEach((el) => { el.textContent = ""; });
  }

  function showPreview(dive) {
    $("#dive-preview-chart").innerHTML = diveProfileHtml(dive, { units });
    attachDiveProfiles($("#dive-preview-chart"));
    show("preview");
  }

  async function previewAndKeep(payload, { onFieldError = null, fromLog = null } = {}) {
    showError("");
    const request = api.post("/dive-profiles/preview", payload);
    pending = request;
    try {
      const dive = await request;
      sending = payload;
      showPreview(dive);
      if (fromLog && onLogDive) onLogDive(fromLog);
    } catch (err) {
      const fields = err instanceof ApiError ? err.fields : {};
      if (!(onFieldError && onFieldError(fields))) {
        showError(err instanceof ApiError ? err.message : "Something went wrong.");
      }
    } finally {
      if (pending === request) pending = null;
    }
  }

  // ---- from a log file ----

  fileInput.addEventListener("change", async () => {
    const file = fileInput.files[0];
    fileInput.value = ""; // choosing the same file again still fires change
    if (!file) return;
    showError("");
    try {
      log = readLog(await readFileText(file));
    } catch (err) {
      showError(err instanceof DiveLogError ? err.message : "That file couldn't be read.");
      return;
    }
    if (log.dives.length === 1) {
      previewAndKeep(cutDive(log, log.dives[0].index), { fromLog: log.dives[0] });
      return;
    }
    const start = preselectedDive(log.dives, eventDay);
    choice.innerHTML = log.dives.map((d) => {
      const when = formatDiveStart(d.startedAt) || "No date";
      const what = [formatDiveDuration(d.durationS), formatDepth(d.maxDepthM, units)].filter(Boolean).join(" · ");
      return `<option value="${d.index}"${d === start ? " selected" : ""}>${esc(when)}${what ? ` — ${esc(what)}` : ""}</option>`;
    }).join("");
    show("list");
  });

  $("#dive-use").addEventListener("click", () => {
    try {
      const index = Number(choice.value);
      previewAndKeep(cutDive(log, index), { fromLog: log.dives.find((d) => d.index === index) });
    } catch (err) {
      showError(err.message);
    }
  });
  choice.addEventListener("dblclick", () => $("#dive-use").click());
  $("#dive-list-cancel").addEventListener("click", () => { log = null; show("choose"); });

  // ---- typed in ----

  function paintEntryUnits() {
    container.querySelectorAll("[data-entry-units]").forEach((b) => {
      const on = b.dataset.entryUnits === entryUnits;
      b.classList.toggle("active", on);
      b.setAttribute("aria-pressed", String(on));
    });
    for (const d of DETAILS) {
      const label = d.quantity && container.querySelector(`[data-unit-of="${d.id}"]`);
      if (label) label.textContent = `(${unitLabel(d.quantity, entryUnits)})`;
    }
  }

  container.querySelectorAll("[data-entry-units]").forEach((button) => {
    button.addEventListener("click", () => {
      const next = button.dataset.entryUnits;
      if (next === entryUnits) return;
      // What's typed stays the same depth, temperature or pressure.
      for (const d of DETAILS.filter((x) => x.quantity)) {
        const el = $(`#${d.id}`);
        if (el.value === "" || !Number.isFinite(Number(el.value))) continue;
        const metric = TO_METRIC[d.quantity](Number(el.value), entryUnits);
        el.value = String(round(TO_DISPLAY[d.quantity](metric, next), 1));
      }
      entryUnits = next;
      paintEntryUnits();
    });
  });

  $("#dive-gas").addEventListener("change", () => {
    $("#dive-o2-wrap").hidden = $("#dive-gas").value !== "nitrox";
  });

  $("#dive-enter").addEventListener("click", () => {
    showError("");
    paintEntryUnits();
    show("details");
    $("#dive-duration").focus();
  });
  $("#dive-details-cancel").addEventListener("click", () => { clearFieldErrors(); show("choose"); });

  /** The details as the API takes them: metric, duration in seconds. */
  function typedDetails() {
    const manual = { units: entryUnits };
    for (const d of DETAILS) {
      const raw = $(`#${d.id}`).value.trim();
      if (raw === "") continue;
      if (d.field === "started_at") manual.started_at = raw;
      else if (d.field === "duration_s") manual.duration_s = Number(raw) * 60;
      else if (d.field === "o2") continue;
      else manual[d.field] = round(TO_METRIC[d.quantity](Number(raw), entryUnits), 2);
    }
    const gas = $("#dive-gas").value;
    if (gas === "air") manual.o2 = 21;
    if (gas === "nitrox" && $("#dive-o2").value.trim() !== "") manual.o2 = Number($("#dive-o2").value);
    return manual;
  }

  $("#dive-details-preview").addEventListener("click", () => {
    clearFieldErrors();
    previewAndKeep({ manual: typedDetails() }, { onFieldError: (fields) => {
      let placed = false;
      for (const [field, message] of Object.entries(fields ?? {})) {
        const d = DETAILS.find((x) => x.field === field);
        if (!d) continue;
        $(`#${d.id}`).classList.add("is-invalid");
        container.querySelector(`[data-error-for="${d.id}"]`).textContent = message;
        placed = true;
      }
      return placed;
    } });
  });

  // ---- taking it off ----

  $("#dive-remove").addEventListener("click", () => {
    if (sending === undefined && existing) removed = true;
    sending = undefined;
    log = null;
    show("choose");
  });

  if (existing) showPreview(existing);
  else show("choose");

  return {
    /** "YYYY-MM-DD": the dive event's day, to start the list on its dive. */
    setEventDay(day) { eventDay = day; },
    async value() {
      if (pending) {
        try { await pending; } catch (_) { /* its failure is already on screen */ }
      }
      if (sending !== undefined) return sending;
      return removed ? null : undefined;
    },
  };
}
